// Shared auth context shape and the provider-facing value contract.
import { createContext } from 'react';
import { User, Session } from '@supabase/supabase-js';
import type { VerificationRecoveryResult } from '@/modules/auth';

export type AppRole = 'super_admin' | 'business_admin' | 'staff' | 'member';

export interface UserRole {
  id: string;
  role: AppRole;
  business_id: string | null;
  facility_id: string | null;
}

export interface Profile {
  id: string;
  user_id: string;
  email: string;
  full_name: string | null;
  phone: string | null;
  avatar_url: string | null;
}

export interface AuthContextType {
  user: User | null;
  session: Session | null;
  profile: Profile | null;
  roles: UserRole[];
  isLoading: boolean;
  isSuperAdmin: boolean;
  isBusinessAdmin: boolean;
  isStaff: boolean;
  signUp: (
    email: string,
    password: string,
    fullName?: string,
  ) => Promise<{ error: Error | null; userId?: string; emailConfirmationRequired: boolean }>;
  signIn: (email: string, password: string) => Promise<{ error: Error | null }>;
  requestPasswordReset: (email: string) => Promise<{ error: Error | null }>;
  getVerificationRecoveryStatus: (email: string) => Promise<VerificationRecoveryResult>;
  requestVerificationEmail: (email: string, redirectTo?: string) => Promise<VerificationRecoveryResult>;
  updatePassword: (password: string) => Promise<{ error: Error | null }>;
  signOut: () => Promise<void>;
  getBusinessIds: (role: AppRole) => string[];
  getFacilityIds: (role: AppRole) => string[];
  hasFacilityRole: (role: AppRole, facilityId: string) => boolean;
}

export const AuthContext = createContext<AuthContextType | undefined>(undefined);
